import * as Sentry from '@sentry/react'
import React, { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router'

import AccessControlGuard from '@/components/guards/AccessControlGuard'
import AuthGuard from '@/components/guards/AuthGuard'
import CompanyAdminGuard from '@/components/guards/CompanyAdminGuard'
import CompassGuard from '@/components/guards/CompassGuard'
import GuestGuard from '@/components/guards/GuestGuard'
import AppLayout from '@/components/layouts/AppLayout'
import ClientDashboardLayout from '@/components/layouts/DashboardLayout/ClientDashboardLayout'
import CompanyDashboardClientReferralsLayout from '@/components/layouts/DashboardLayout/CompanyDashboardClientReferralsLayout/CompanyDashboardClientReferralsLayout'
import CompanyDashboardCompanyCompassLayout from '@/components/layouts/DashboardLayout/CompanyDashboardCompassLayout/CompanyDashboardCompanyCompassLayout'
import CompanyDashboardLayout from '@/components/layouts/DashboardLayout/CompanyDashboardLayout'
import CompanyDashboardProfessionalNetworkLayout from '@/components/layouts/DashboardLayout/CompanyDashboardProfessionalNetworkLayout/components/CompanyDashboardProfessionalNetworkLayout'
import GroupDashboardLayout from '@/components/layouts/DashboardLayout/GroupDashboardLayout'
import IcaewDashboardLayout from '@/components/layouts/DashboardLayout/IcaewDashboardLayout'
import NetworkDashboardLayout from '@/components/layouts/DashboardLayout/NetworkDashboardLayout'
import LandingDarkLayout from '@/components/layouts/LandingDarkLayout/LandingDarkLayout'
import LandingGreyLayout from '@/components/layouts/LandingGreyLayout'
import LandingWhiteLayout from '@/components/layouts/LandingWhiteLayout/LandingWhiteLayout'
import MinimalLayout from '@/components/layouts/MinimalLayout'
import OfficeLayout from '@/components/layouts/OfficeLayout/OfficeLayout'
import ReferralInviteLayout from '@/components/layouts/ReferralInviteLayout/ReferralInviteLayout'
import RqCertifiedLayout from '@/components/layouts/RqCertifiedLayout/RqCertifiedLayout'
import ReferralWithESignatureDetailsProvider from '@/components/misc/ReferralWithESignatureDetailsProvider'
import SentryErrorBoundary from '@/components/misc/SentryErrorBoundary'
import GlobalModals from '@/components/modals/GlobalModals'
import SkeletonPage from '@/components/skeleton/SkeletonPage'
import LoadingWidget from '@/components/widgets/LoadingWidget'
import { usePageViewListener } from '@/hooks/usePageViewListener'
import { useRestoreScrollPosition } from '@/hooks/useRestoreScrollPosition'
import { DOM_IDS } from '@/lib/constants'
import { isNotProduction } from '@/lib/helpers/envHelpers'
import { ROUTES } from '@/lib/routes'
import IndexPage from '@/pages/IndexPage'
import RedirectPage from '@/pages/redirect/RedirectPage'
import { USER_TYPE } from '@/types/misc'

const AddCompanyPage = lazy(() => import('@/pages/AddCompanyPage'))

const AddLocationPage = lazy(
  () => import('@/pages/addLocation/AddLocationPage'),
)

const AddRelationshipPage = lazy(() => import('@/pages/AddRelationshipPage'))

const EditLocationPage = lazy(
  () => import('@/pages/editLocation/EditLocationPage'),
)

const AddExistingAdviserPage = lazy(
  () => import('@/pages/AddExistingAdviserPage'),
)

const AddRelationshipAgreementPage = lazy(
  () => import('@/pages/addRelationshipAgreement/AddRelationshipAgreementPage'),
)

const AutoProvisionDomainVerificationPage = lazy(
  () =>
    import('@/pages/autoProvisionDomainVerification/AutoProvisionDomainVerificationPage'),
)

const CalendarSettingsPage = lazy(
  () => import('@/pages/calendarSettingsPage/CalendarSettingsPage'),
)

const ChallengePage = lazy(() => import('@/pages/challenge/ChallengePage'))

const ClientPaymentPage = lazy(
  () => import('@/pages/clientPayment/ClientPaymentPage'),
)

const ClientsPage = lazy(() => import('@/pages/clients/ClientsPage'))

const ClientsImportPage = lazy(
  () => import('@/pages/clientsImport/ClientsImportPage'),
)

const CommercialAgreementEditPage = lazy(
  () => import('@/pages/commercialAgreementEdit/CommercialAgreementEditPage'),
)

const CommercialAgreementTemplateReviewPage = lazy(
  () =>
    import('@/pages/commercialAgreementTemplateReview/CommercialAgreementTemplateReviewPage'),
)

const CommercialAgreementTemplateRevisionsPage = lazy(
  () =>
    import('@/pages/commercialAgreementTemplateRevisions/CommercialAgreementTemplateRevisionsPage'),
)

const CompanyDashboardPage = lazy(
  () => import('@/pages/companyDashboard/CompanyDashboardPage'),
)

const CompanyProfilePage = lazy(
  () => import('@/pages/companyProfile/CompanyProfilePage'),
)

const CompleteRegistrationPage = lazy(
  () => import('@/pages/CompleteRegistrationPage'),
)

const CompanyCompassPage = lazy(
  () => import('@/pages/companyCompass/CompanyCompassPage'),
)

const CompanyCompassViewPage = lazy(
  () => import('@/pages/companyCompassView/CompanyCompassViewPage'),
)

const CompanyCompassAdminPage = lazy(
  () => import('@/pages/companyCompassAdmin/CompanyCompassAdminPage'),
)

const CompanyCompassDashboardPage = lazy(
  () => import('@/pages/companyCompassDashboard/CompanyCompassDashboardPage'),
)

const CompanyCompassGlobalLinkPage = lazy(
  () => import('@/pages/companyCompassGlobalLink/CompanyCompassGlobalLinkPage'),
)

const CompanyCompassInvitePage = lazy(
  () => import('@/pages/companyCompassInvite/CompanyCompassInvitePage'),
)

const CompanyCompassInvitePreviewPage = lazy(
  () =>
    import('@/pages/companyCompassInvitePreview/CompanyCompassInvitePreviewPage'),
)

const CompanyCompassSnapshotsPage = lazy(
  () => import('@/pages/companyCompassSnapshots/CompanyCompassSnapshotsPage'),
)

const CompanySuspendedPage = lazy(() => import('@/pages/CompanySuspendedPage'))

const CompassActionsPage = lazy(
  () => import('@/pages/compassActions/CompassActionsPage'),
)

const CompassConfirmSharingPage = lazy(
  () => import('@/pages/compassConfirmSharing/CompassConfirmSharingPage'),
)

const CompassConfirmStartPage = lazy(
  () => import('@/pages/compassConfirmStart/CompassConfirmStartPage'),
)

const CompassConfirmSharingViaGlobalLinkPage = lazy(
  () =>
    import('@/pages/compassConfirmSharingViaGlobalLink/CompassConfirmSharingViaGlobalLinkPage'),
)

const CompassDashboardPage = lazy(
  () => import('@/pages/compassDashboard/CompassDashboardPage'),
)

const CompassDetailsAlreadyOnFilePage = lazy(
  () =>
    import('@/pages/compassDetailsAlreadyOnFile/CompassDetailsAlreadyOnFilePage'),
)

const CompassHowItWorksPage = lazy(
  () => import('@/pages/compassHowItWorks/CompassHowItWorksPage'),
)

const CompassPartnersPage = lazy(
  () => import('@/pages/compassPartners/CompassPartnersPage'),
)

const CompassPdfGuidePage = lazy(
  () => import('@/pages/compassPdfGuide/CompassPdfGuidePage'),
)

const CompassPdfReportPreviewPage = lazy(
  () => import('@/pages/compassPdfReportPreview/CompassPdfReportPreviewPage'),
)

const CompassPdfReportSectionPage = lazy(
  () => import('@/pages/compassPdfReportSection/CompassPdfReportSectionPage'),
)

const CompassRedirectPage = lazy(
  () => import('@/pages/compassRedirect/CompassRedirectPage'),
)

const CompassSharingPage = lazy(
  () => import('@/pages/compassSharing/CompassSharingPage'),
)

const CompassSnapshotPage = lazy(
  () => import('@/pages/compassSnapshot/CompassSnapshotPage'),
)

const CompassSnapshotsPage = lazy(
  () => import('@/pages/compassSnapshots/CompassSnapshotsPage'),
)

const CompassStartPage = lazy(
  () => import('@/pages/compassStart/CompassStartPage'),
)

const CompassUpdatePage = lazy(
  () => import('@/pages/compassUpdate/CompassUpdatePage'),
)

const ComponentReferencePage = lazy(
  () => import('@/pages/componentReference/ComponentReferencePage'),
)

const ConfirmConsentPage = lazy(
  () => import('@/pages/confirmConsent/ConfirmConsentPage'),
)

const DebugPage = lazy(() => import('@/pages/debug/DebugPage'))

const DemoVideosPage = lazy(() => import('@/pages/demoVideos/DemoVideosPage'))

const DirectLeadCapturePage = lazy(
  () => import('@/pages/contactUs/ContactUsPage'),
)

const DirectLeadCaptureEmbedPage = lazy(
  () => import('@/pages/directLeadCaptureEmbed/DirectLeadCaptureEmbedPage'),
)

const DisclaimerPage = lazy(() => import('@/pages/DisclaimerPage'))

const DomainAccessDeniedPage = lazy(
  () => import('@/pages/DomainAccessDeniedPage'),
)
const EmailAndCalendarIntegrationReconnectPage = lazy(
  () =>
    import('@/pages/emailAndCalendarIntegrationReconnect/EmailAndCalendarIntegrationReconnectPage'),
)

const FindProfessionalPage = lazy(() => import('@/pages/FindProfessionalPage'))

const GroupDashboardPage = lazy(
  () => import('@/pages/groupDashboard/GroupDashboardPage'),
)

const GroupPreferencesPage = lazy(
  () => import('@/pages/groupPreferences/GroupPreferencesPage'),
)

const GroupOverviewPage = lazy(
  () => import('@/pages/groupOverview/GroupOverviewPage'),
)

const HelpPage = lazy(() => import('@/pages/help/HelpPage'))

const NetworkOverviewPage = lazy(
  () => import('@/pages/networkOverview/NetworkOverviewPage'),
)

const NetworkDashboardPage = lazy(
  () => import('@/pages/networkDashboard/NetworkDashboardPage'),
)

const NetworkReferralRegisterPage = lazy(
  () => import('@/pages/networkReferralRegister/NetworkReferralRegisterPage'),
)

const MobileLandingPage = lazy(() => import('@/pages/mobile/MobileLandingPage'))

const HubSpotIntegrationPage = lazy(
  () => import('@/pages/hubspotIntegration/HubSpotIntegrationPage'),
)

const HubSpotIntegrationRegisterPage = lazy(
  () =>
    import('@/pages/hubspotIntegrationRegister/HubSpotIntegrationRegisterPage'),
)

const IcaewOverviewPage = lazy(() => import('@/pages/IcaewOverviewPage'))

const IcaewFirmSearchPage = lazy(
  () => import('@/pages/icaewFirmSearch/IcaewFirmSearchPage'),
)

const IcaewTrendsPage = lazy(() => import('@/pages/IcaewTrendsPage'))

const IntellifloIntegrationPage = lazy(
  () => import('@/pages/intellifloIntegration/IntellifloIntegrationPage'),
)

const IntellifloIntegrationQueuePage = lazy(
  () =>
    import('@/pages/intellifloIntegrationQueue/IntellifloIntegrationQueuePage'),
)

const IntellifloIntegrationRedirectPage = lazy(
  () =>
    import('@/pages/intellifloIntegrationRedirect/IntellifloIntegrationRedirectPage'),
)

const IntellifloIntegrationAuthFlowPage = lazy(
  () =>
    import('@/pages/intellifloIntegrationAuthFlow/IntellifloIntegrationAuthFlowPage'),
)

const InvalidOrExpiredPage = lazy(() => import('@/pages/InvalidOrExpiredPage'))

const CompanyInvitationPage = lazy(
  () => import('@/pages/companyInvitation/CompanyInvitationPage'),
)

const InviteUserPage = lazy(() => import('@/pages/inviteUser/InviteUserPage'))
const InviteGroupUserPage = lazy(
  () => import('@/pages/inviteGroupUser/InviteGroupUserPage'),
)

const ManageLocationsPage = lazy(
  () => import('@/pages/manageLocations/ManageLocationsPage'),
)

const ManageReferralStatusesPage = lazy(
  () => import('@/pages/manageReferralStatuses/ManageReferralStatusesPage'),
)

const ManageUsersPage = lazy(
  () => import('@/pages/manageUsers/ManageUsersPage'),
)

const ManageUserProvisioningPage = lazy(
  () => import('@/pages/manageUsers/ManageUserProvisioningPage'),
)

const OfficeCheckAuthPage = lazy(
  () => import('@/pages/officeTaskPane/OfficeCheckAuthPage'),
)

const OfficeTaskPanePage = lazy(
  () => import('@/pages/officeTaskPane/OfficeTaskPanePage'),
)

const OfficeConnectEmailAndCalendarPage = lazy(
  () =>
    import('@/pages/officeConnectEmailAndCalendar/OfficeConnectEmailAndCalendarPage'),
)

const OfficeReferralConfirmationPage = lazy(
  () =>
    import('@/pages/officeReferralConfirmation/OfficeReferralConfirmationPage'),
)

const OfficeInaccessibleReferralConfirmationPage = lazy(
  () =>
    import('@/pages/officeInaccessibleReferralConfirmation/OfficeInaccessibleReferralConfirmationPage'),
)

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

const PlannrIntegrationPage = lazy(
  () => import('@/pages/plannrIntegration/PlannrIntegrationPage'),
)

const PlannrIntegrationRegisterPage = lazy(
  () =>
    import('@/pages/plannrIntegrationRegister/PlannrIntegrationRegisterPage'),
)

const XeroIntegrationPage = lazy(
  () => import('@/pages/xeroIntegration/XeroIntegrationPage'),
)

const XeroIntegrationRegisterPage = lazy(
  () => import('@/pages/xeroIntegrationRegister/XeroIntegrationRegisterPage'),
)

const PreferencesPage = lazy(
  () => import('@/pages/preferences/PreferencesPage'),
)

const ProfessionalNetworkApprovalManagementPage = lazy(
  () =>
    import('@/pages/professionalNetworkApprovalManagement/ProfessionalNetworkApprovalManagementPage'),
)

const ProfessionalNetworkInvitePage = lazy(
  () =>
    import('@/pages/professionalNetworkInvite/ProfessionalNetworkInvitePage'),
)

const ProfessionalNetworkOnboardingPage = lazy(
  () =>
    import('@/pages/professionalNetworkOnboardingPage/ProfessionalNetworkOnboardingPage'),
)

const ProfessionalNetworkRelationshipsPage = lazy(
  () =>
    import('@/pages/professionalNetworkRelationships/ProfessionalNetworkRelationshipsPage'),
)

const ProfessionalNetworkApprovalRequestsPage = lazy(
  () =>
    import('@/pages/professionalNetworkApprovalRequests/ProfessionalNetworkApprovalRequestsPage'),
)

const ProfessionalNetworkStatsPage = lazy(
  () => import('@/pages/professionalNetworkStats/ProfessionalNetworkStatsPage'),
)

const ProfessionalNetworkRevenuePage = lazy(
  () =>
    import('@/pages/professionalNetworkRevenue/ProfessionalNetworkRevenuePage'),
)

const RecommendationsPage = lazy(
  () => import('@/pages/recommendations/RecommendationsPage'),
)

const RecommendationGenerateLinkPage = lazy(
  () =>
    import('@/pages/recommendationGenerateLink/RecommendationGenerateLinkPage'),
)

const RecommendationsSettingsPage = lazy(
  () => import('@/pages/recommendationsSettings/RecommendationsSettingsPage'),
)

const ReferralAffiliateLinkPage = lazy(
  () => import('@/pages/ReferralAffiliateLinkPage'),
)

const ReferAFriendPage = lazy(
  () => import('@/pages/referAFriend/ReferAFriendPage'),
)

const ReferAFriendSharePage = lazy(
  () => import('@/pages/referAFriendShare/ReferAFriendSharePage'),
)

const ReferClientPage = lazy(
  () => import('@/pages/referClient/ReferClientPage'),
)

const ReferralClientConsentPdfPage = lazy(
  () =>
    import('@/pages/referralESignatureConsentPdf/ReferralESignatureConsentPdfPage'),
)

const ReferralClientConsentPdfPreviewPage = lazy(
  () =>
    import('@/pages/referralESignatureConsentPdfPreview/ReferralESignatureConsentPdfPreviewPage'),
)

const ReferralCommissionConsentPage = lazy(
  () =>
    import('@/pages/referralCommissionConsent/ReferralCommissionConsentPage'),
)

const ReferralESignatureConsentPage = lazy(
  () =>
    import('@/pages/referralESignatureConsent/ReferralESignatureConsentPage'),
)

const ReferralFeedbackPage = lazy(
  () => import('@/pages/referralFeedback/ReferralFeedbackPage'),
)

const ReferralLinkPage = lazy(
  () => import('@/pages/referralLink/ReferralLinkPage'),
)

const ReferralPagesPage = lazy(
  () => import('@/pages/referralPagesPage/ReferralPagesPage'),
)

const ReferralRoutingPage = lazy(
  () => import('@/pages/referralRouting/ReferralRoutingPage'),
)

const ReferralsPage = lazy(() => import('@/pages/referrals/ReferralsPage'))

const ReferralDiscoveryPage = lazy(
  () => import('@/pages/referralDiscovery/ReferralDiscoveryPage'),
)

const ReferralsCsvExportPage = lazy(
  () => import('@/pages/referralsCsvExport/ReferralsCsvExportPage'),
)

const ReferralInvitesPage = lazy(
  () => import('@/pages/referralInvites/ReferralInvitesPage'),
)

const ReferralInvitePage = lazy(
  () => import('@/pages/referralInvite/ReferralInvitePage'),
)

const ReferralRetrospectiveESignatureConsentPage = lazy(
  () =>
    import('@/pages/referralRetrospectiveESignatureConsent/ReferralRetrospectiveESignatureConsentPage'),
)

const ReferralSummaryPage = lazy(
  () => import('@/pages/referralSummary/ReferralSummaryPage'),
)

const CompanyRegistrationPage = lazy(
  () => import('@/pages/CompanyRegistrationPage'),
)

const RedirectNewUserPage = lazy(() => import('@/pages/RedirectNewUserPage'))

const RescheduleMeetingPage = lazy(
  () => import('@/pages/rescheduleMeeting/RescheduleMeetingPage'),
)

const RequestCallbackPage = lazy(
  () => import('@/pages/requestCallback/RequestCallbackPage'),
)

const RqCertifiedApplicationStatusPage = lazy(
  () =>
    import('@/pages/rqCertifiedApplicationStatus/RqCertifiedApplicationStatusPage'),
)

const RqCertifiedApplicationProcessPage = lazy(
  () =>
    import('@/pages/rqCertifiedApplicationProcess/RqCertifiedApplicationProcessPage'),
)

const RqCertifiedGlossaryPage = lazy(
  () => import('@/pages/rqCertifiedGlossary/RqCertifiedGlossaryPage'),
)

const RqCertifiedNdaPdfPage = lazy(
  () => import('@/pages/rqCertifiedNdaPdf/RqCertifiedNdaPdfPage'),
)

const RqCertifiedPdfGuidesPage = lazy(
  () => import('@/pages/rqCertifiedPdfGuides/RqCertifiedPdfGuidesPage'),
)

const ShortlistPage = lazy(() => import('@/pages/ShortlistPage'))

const SignInPage = lazy(() => import('@/pages/SignInPage'))

const SignOutPage = lazy(() => import('@/pages/SignOutPage'))

const SignUpPage = lazy(() => import('@/pages/signUp/SignUpPage'))

const UserCompassPage = lazy(
  () => import('@/pages/userCompass/UserCompassPage'),
)

const VerifyPublicEmailPage = lazy(
  () => import('@/pages/verifyPublicEmail/VerifyPublicEmailPage'),
)

const WalkthroughPage = lazy(
  () => import('@/pages/walkthrough/WalkthroughPage'),
)

const WelcomePage = lazy(() => import('@/pages/WelcomePage'))

const WithdrawGdprConsentPage = lazy(
  () => import('@/pages/withdrawGdprConsent/WithdrawGdprConsentPage'),
)

const InvitationsPage = lazy(
  () => import('@/pages/invitations/InvitationsPage'),
)

const AccountSettingsIframePage = lazy(
  () => import('@/embed/iframePages/AccountSettingsIframePage'),
)

const MappingIframePage = lazy(
  () => import('@/embed/iframePages/MappingIframePage'),
)

const IntroducersIframePage = lazy(
  () => import('@/embed/iframePages/IntroducersIframePage'),
)

const ReferralIframePage = lazy(
  () => import('@/embed/iframePages/ReferralIframePage'),
)

const ClientReferralsCompanyOverviewIframePage = lazy(
  () => import('@/embed/iframePages/ClientReferralsCompanyOverviewIframePage'),
)

const ClientReferralsCompanyOverviewReferralsIframePage = lazy(
  () =>
    import('@/embed/iframePages/ClientReferralsCompanyOverviewReferralsIframePage'),
)

const ClientReferralsIframePage = lazy(
  () => import('@/embed/iframePages/ClientReferralsIframePage'),
)

const ReferIframePage = lazy(
  () => import('@/embed/iframePages/ReferIframePage'),
)

const ReferSuccessIframePage = lazy(
  () => import('@/embed/iframePages/ReferSuccessIframePage'),
)

const XplanIntegrationPage = lazy(
  () => import('@/pages/xplanIntegration/XplanIntegrationPage'),
)

const XplanIntegrationRedirectPage = lazy(
  () => import('@/pages/xplanIntegrationRedirect/XplanIntegrationRedirectPage'),
)

interface AppRoute {
  path: string
  element: React.ReactNode
}

type GroupDashboardRoute = AppRoute & {
  padded?: boolean
}

type NetworkDashboardRoute = AppRoute & {
  padded?: boolean
}

type CompanyDashboardRoute = AppRoute & {
  padded?: boolean
  fullscreen?: boolean
}

const accountSetupRoutes: AppRoute[] = [
  {
    path: ROUTES._companyRegistration,
    element: (
      <LandingGreyLayout variant="card">
        <CompanyRegistrationPage />
      </LandingGreyLayout>
    ),
  },
  {
    path: ROUTES._welcome,
    element: (
      // Welcome is about the current company, so group and network users (who
      // have none) are sent home instead
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <LandingGreyLayout variant="card">
          <WelcomePage />
        </LandingGreyLayout>
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._invitations,
    element: (
      <LandingDarkLayout>
        <MinimalLayout>
          <InvitationsPage />
        </MinimalLayout>
      </LandingDarkLayout>
    ),
  },
  {
    path: ROUTES._redirectNewUser,
    element: <RedirectNewUserPage />,
  },
]

const guestRoutes: AppRoute[] = [
  {
    path: ROUTES.index(),
    element: <IndexPage />,
  },
  {
    path: ROUTES._debug,
    element: (
      <AccessControlGuard
        guard={isNotProduction()}
        onDenied={{ redirect: { to: ROUTES.index() } }}
      >
        <DebugPage />
      </AccessControlGuard>
    ),
  },
  {
    path: ROUTES._componentReference,
    element: (
      <AccessControlGuard
        guard={isNotProduction()}
        onDenied={{ redirect: { to: ROUTES.index() } }}
      >
        <ComponentReferencePage />
      </AccessControlGuard>
    ),
  },
  {
    path: ROUTES._signIn,
    element: (
      <GuestGuard>
        <SignInPage />
      </GuestGuard>
    ),
  },
  {
    path: ROUTES._signOut,
    element: <SignOutPage />,
  },
  {
    path: ROUTES._signUp,
    element: <SignUpPage />,
  },
]

const groupDashboardRoutes: GroupDashboardRoute[] = [
  {
    path: ROUTES._groupDashboard,
    element: <GroupDashboardPage />,
    padded: false,
  },
  { path: ROUTES._groupOverview, element: <GroupOverviewPage /> },
  { path: ROUTES._groupPreferences, element: <GroupPreferencesPage /> },
]

const networkDashboardRoutes: NetworkDashboardRoute[] = [
  { path: ROUTES._networkOverview, element: <NetworkOverviewPage /> },
  { path: ROUTES._networkDashboard, element: <NetworkDashboardPage /> },
  {
    path: ROUTES._networkReferralRegister,
    element: <NetworkReferralRegisterPage />,
  },
]

const companyAndGroupDashboardRoutes: Array<
  CompanyDashboardRoute | GroupDashboardRoute
> = [
  { path: ROUTES._help, element: <HelpPage /> },
  { path: ROUTES._manageUsers, element: <ManageUsersPage /> },
  { path: ROUTES._referrals, element: <ReferralsPage /> },
  { path: ROUTES._referralSummary, element: <ReferralSummaryPage /> },
]

const companyDashboardRoutes: CompanyDashboardRoute[] = [
  { path: ROUTES._addCompany, element: <AddCompanyPage /> },
  {
    path: ROUTES._addLocation,
    element: (
      <CompanyAdminGuard>
        <AddLocationPage />
      </CompanyAdminGuard>
    ),
  },
  {
    path: ROUTES._editLocation,
    element: (
      <CompanyAdminGuard>
        <EditLocationPage />
      </CompanyAdminGuard>
    ),
  },
  { path: ROUTES._addExistingAdviser, element: <AddExistingAdviserPage /> },
  {
    path: ROUTES._addRelationshipAgreement,
    element: <AddRelationshipAgreementPage />,
  },
  { path: ROUTES._addRelationship, element: <AddRelationshipPage /> },
  {
    path: ROUTES._calendarSettingsPage,
    element: <CalendarSettingsPage />,
  },
  { path: ROUTES._clients, element: <ClientsPage /> },
  { path: ROUTES._clientsImport, element: <ClientsImportPage /> },
  {
    path: ROUTES._commercialAgreementEdit,
    element: (
      <Suspense fallback={<SkeletonPage />}>
        <CommercialAgreementEditPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES._commercialAgreementTemplateReview,
    element: <CommercialAgreementTemplateReviewPage />,
    padded: false,
    fullscreen: true,
  },
  {
    path: ROUTES._companyDashboard,
    element: <CompanyDashboardPage />,
    padded: false,
  },
  { path: ROUTES._companyProfile, element: <CompanyProfilePage /> },
  {
    path: ROUTES._compassConfirmStart,
    element: <CompassConfirmStartPage />,
  },
  { path: ROUTES._demoVideos, element: <DemoVideosPage /> },
  { path: ROUTES._referralDiscovery, element: <ReferralDiscoveryPage /> },
  { path: ROUTES._disclaimer, element: <DisclaimerPage /> },
  { path: ROUTES._findProfessional, element: <FindProfessionalPage /> },
  { path: ROUTES._hubSpotIntegration, element: <HubSpotIntegrationPage /> },
  {
    path: ROUTES._hubSpotIntegrationRegister,
    element: <HubSpotIntegrationRegisterPage />,
  },
  {
    path: ROUTES._intellifloIntegrationAuthCallback,
    padded: false,
    element: <IntellifloIntegrationAuthFlowPage />,
  },
  {
    path: ROUTES._intellifloIntegration,
    padded: false,
    element: <IntellifloIntegrationPage />,
  },
  {
    path: ROUTES._intellifloIntegrationQueue,
    padded: false,
    element: (
      <CompanyAdminGuard>
        <IntellifloIntegrationQueuePage />
      </CompanyAdminGuard>
    ),
  },
  {
    path: ROUTES._intellifloIntegrationRedirect,
    element: <IntellifloIntegrationRedirectPage />,
  },
  {
    path: ROUTES._manageLocations,
    element: <ManageLocationsPage />,
  },
  {
    path: ROUTES._manageReferralStatuses,
    element: <ManageReferralStatusesPage />,
  },
  { path: ROUTES._manageUsers, element: <ManageUsersPage /> },
  {
    path: ROUTES._manageUserProvisioning,
    element: (
      <CompanyAdminGuard>
        <ManageUserProvisioningPage />
      </CompanyAdminGuard>
    ),
  },
  { path: ROUTES._mobileLanding, element: <MobileLandingPage /> },
  {
    path: ROUTES._plannrIntegration,
    element: <PlannrIntegrationPage />,
  },
  {
    path: ROUTES._plannrIntegrationRegister,
    element: <PlannrIntegrationRegisterPage />,
  },
  {
    path: ROUTES._referralPagesPage,
    element: <ReferralPagesPage />,
  },
  {
    path: ROUTES._referralRoutingPage,
    element: <ReferralRoutingPage />,
  },
  { path: ROUTES._preferences, element: <PreferencesPage /> },
  { path: ROUTES._referrals, element: <ReferralsPage /> },
  {
    path: ROUTES._referClient,
    element: <ReferClientPage />,
    padded: false,
  },
  {
    path: ROUTES._rqCertifiedApplicationProcess,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedApplicationProcessPage />
      </RqCertifiedLayout>
    ),
  },
  {
    path: ROUTES._rqCertifiedApplicationStatus,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedApplicationStatusPage />
      </RqCertifiedLayout>
    ),
  },
  {
    path: ROUTES._rqCertifiedGlossary,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedGlossaryPage />
      </RqCertifiedLayout>
    ),
  },
  {
    path: ROUTES._rqCertifiedPdfGuides,
    element: (
      <RqCertifiedLayout>
        <RqCertifiedPdfGuidesPage />
      </RqCertifiedLayout>
    ),
  },
  {
    path: ROUTES._xplanIntegration,
    element: <XplanIntegrationPage />,
  },
  {
    path: ROUTES._xeroIntegration,
    element: <XeroIntegrationPage />,
  },
  {
    path: ROUTES._xeroIntegrationRegister,
    element: <XeroIntegrationRegisterPage />,
  },
]

const companyDashboardCompanyCompassRoutes: AppRoute[] = [
  {
    path: ROUTES._companyCompassAdmin,
    element: <CompanyCompassAdminPage />,
  },
  {
    path: ROUTES._companyCompassDashboard,
    element: <CompanyCompassDashboardPage />,
  },
  {
    path: ROUTES._companyCompassGlobalLink,
    element: <CompanyCompassGlobalLinkPage />,
  },
  {
    path: ROUTES._companyCompassSnapshots,
    element: <CompanyCompassSnapshotsPage />,
  },
]

const companyDashboardProfessionalNetworkRoutes: AppRoute[] = [
  {
    path: ROUTES._professionalNetworkApprovalManagement,
    element: <ProfessionalNetworkApprovalManagementPage />,
  },
  {
    path: ROUTES._professionalNetworkInvite,
    element: <ProfessionalNetworkInvitePage />,
  },
  {
    path: ROUTES._professionalNetworkRelationships,
    element: <ProfessionalNetworkRelationshipsPage />,
  },
  {
    path: ROUTES._professionalNetworkOnboarding,
    element: <ProfessionalNetworkOnboardingPage />,
  },

  {
    path: ROUTES._professionalNetworkApprovalRequests,
    element: <ProfessionalNetworkApprovalRequestsPage />,
  },
  {
    path: ROUTES._professionalNetworkStats,
    element: <ProfessionalNetworkStatsPage />,
  },
  {
    path: ROUTES._professionalNetworkRevenue,
    element: <ProfessionalNetworkRevenuePage />,
  },
]

const companyDashboardRecommendationRoutes: AppRoute[] = [
  {
    path: ROUTES._recommendations,
    element: <RecommendationsPage />,
  },
  {
    path: ROUTES._recommendationsSettings,
    element: <RecommendationsSettingsPage />,
  },
]

const clientDashboardRoutes: AppRoute[] = [
  { path: ROUTES._compassActions, element: <CompassActionsPage /> },
  {
    path: ROUTES._compassConfirmSharing,
    element: <CompassConfirmSharingPage />,
  },
  {
    path: ROUTES._compassConfirmSharingViaGlobalLink,
    element: <CompassConfirmSharingViaGlobalLinkPage />,
  },
  { path: ROUTES._compassDashboard, element: <CompassDashboardPage /> },
  {
    path: ROUTES._compassDetailsAlreadyOnFile,
    element: <CompassDetailsAlreadyOnFilePage />,
  },
  { path: ROUTES._compassDisclaimer, element: <DisclaimerPage /> },
  { path: ROUTES._compassHowItWorks, element: <CompassHowItWorksPage /> },
  { path: ROUTES._compassPartners, element: <CompassPartnersPage /> },
  { path: ROUTES._compassSnapshots, element: <CompassSnapshotsPage /> },
  { path: ROUTES._compassSnapshot, element: <CompassSnapshotPage /> },
  { path: ROUTES._compassSharing, element: <CompassSharingPage /> },
  { path: ROUTES._compassStart, element: <CompassStartPage /> },
  { path: ROUTES._compassUpdate, element: <CompassUpdatePage /> },
]

const guestLandingRoutes: AppRoute[] = [
  { path: ROUTES._confirmConsent, element: <ConfirmConsentPage /> },
  {
    path: ROUTES._autoProvisionDomainVerification,
    element: <AutoProvisionDomainVerificationPage />,
  },
  { path: ROUTES._inviteUser, element: <InviteUserPage /> },
  { path: ROUTES._inviteGroupUser, element: <InviteGroupUserPage /> },
  { path: ROUTES._withdrawGdprConsent, element: <WithdrawGdprConsentPage /> },
  { path: ROUTES._verifyPublicEmail, element: <VerifyPublicEmailPage /> },
  { path: ROUTES._clientPayment, element: <ClientPaymentPage /> },
  {
    path: ROUTES._referralAffiliateLink,
    element: <ReferralAffiliateLinkPage />,
  },
]

const guestLandingRoutesWthBranding: AppRoute[] = [
  { path: ROUTES._requestCallback, element: <RequestCallbackPage /> },
  // Renders its own LandingDarkLayout, as does its InvalidOrExpiredPage
  { path: ROUTES._rescheduleMeeting, element: <RescheduleMeetingPage /> },
]

const guestLandingWhiteTemplateRoutes: AppRoute[] = [
  { path: ROUTES._companyInvitation, element: <CompanyInvitationPage /> },
  { path: ROUTES._referralFeedback, element: <ReferralFeedbackPage /> },
]

const guestLandingNoTemplateRoutes: AppRoute[] = [
  {
    path: ROUTES._commissionConsent,
    element: <ReferralCommissionConsentPage />,
  },
]

const companyUserLandingRoutes: AppRoute[] = [
  {
    path: ROUTES._commercialAgreementTemplateRevisions,
    element: (
      <MinimalLayout>
        <CommercialAgreementTemplateRevisionsPage />
      </MinimalLayout>
    ),
  },
  { path: ROUTES._companySuspended, element: <CompanySuspendedPage /> },
  { path: ROUTES._shortlist, element: <ShortlistPage /> },
  {
    path: ROUTES._domainAccessDenied,
    element: (
      <MinimalLayout>
        <DomainAccessDeniedPage />
      </MinimalLayout>
    ),
  },
]

const icaewRoutes: AppRoute[] = [
  { path: ROUTES._icaewDisclaimer, element: <DisclaimerPage /> },
  { path: ROUTES._icaewOverview, element: <IcaewOverviewPage /> },
  { path: ROUTES._icaewFirmSearch, element: <IcaewFirmSearchPage /> },
  { path: ROUTES._icaewTrends, element: <IcaewTrendsPage /> },
]

const referralInviteRoutes: AppRoute[] = [
  { path: ROUTES._referralInvites, element: <ReferralInvitesPage /> },
  {
    path: ROUTES._referralInvite,
    element: <ReferralInvitePage />,
  },
]

const officeRoutes: AppRoute[] = [
  {
    path: ROUTES._officeReferralConfirmation,
    element: <OfficeReferralConfirmationPage />,
  },
  {
    path: ROUTES._officeInaccessibleReferralConfirmation,
    element: <OfficeInaccessibleReferralConfirmationPage />,
  },
  {
    path: ROUTES._officeTaskPane,
    element: <OfficeTaskPanePage />,
  },
  {
    path: ROUTES._officeConnectEmailAndCalendar,
    element: <OfficeConnectEmailAndCalendarPage />,
  },
]

const miscRoutes: AppRoute[] = [
  { path: ROUTES._compassPdfGuide, element: <CompassPdfGuidePage /> },
  {
    path: ROUTES._companyCompass,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompanyCompassPage />
      </AuthGuard>
    ),
  },
  { path: ROUTES._companyCompassInvite, element: <CompanyCompassInvitePage /> },
  {
    path: ROUTES._companyCompassInvitePreview,
    element: <CompanyCompassInvitePreviewPage />,
  },
  {
    path: ROUTES._companyCompassView,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompanyCompassViewPage />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._compassPdfReportPreview,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompassPdfReportPreviewPage />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._compassPdfReportSection,
    element: <CompassPdfReportSectionPage />,
  },
  { path: ROUTES._compassRedirect, element: <CompassRedirectPage /> },
  {
    path: ROUTES._completeRegistration,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompleteRegistrationPage isAtSignup={false} />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._completeRegistrationStep,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompleteRegistrationPage isAtSignup={false} />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._completeRegistrationAtSignup,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompleteRegistrationPage isAtSignup={true} />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._completeRegistrationAtSignupStep,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <CompleteRegistrationPage isAtSignup={true} />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._contactUs,
    element: <DirectLeadCapturePage />,
  },
  {
    path: ROUTES._directLeadCaptureEmbed,
    element: <DirectLeadCaptureEmbedPage />,
  },
  {
    path: ROUTES._emailAndCalendarIntegrationReconnect,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <LandingDarkLayout>
          <MinimalLayout>
            <EmailAndCalendarIntegrationReconnectPage />
          </MinimalLayout>
        </LandingDarkLayout>
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._emailAndCalendarIntegrationReconnected,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <LandingDarkLayout>
          <MinimalLayout>
            <EmailAndCalendarIntegrationReconnectPage />
          </MinimalLayout>
        </LandingDarkLayout>
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._officeCheckAuth,
    element: <OfficeCheckAuthPage />,
  },
  {
    path: ROUTES._redirect,
    element: <RedirectPage />,
  },
  {
    path: ROUTES._recommendationGenerateLink,
    element: <RecommendationGenerateLinkPage />,
  },
  {
    path: ROUTES._shareRecommendation,
    element: <ReferAFriendSharePage />,
  },
  {
    path: ROUTES._referralESignatureConsent,
    element: (
      <ReferralWithESignatureDetailsProvider>
        <ReferralESignatureConsentPage />
      </ReferralWithESignatureDetailsProvider>
    ),
  },
  {
    path: ROUTES._referralESignatureConsentPdf,
    element: (
      <ReferralWithESignatureDetailsProvider>
        <ReferralClientConsentPdfPage />
      </ReferralWithESignatureDetailsProvider>
    ),
  },
  {
    path: ROUTES._referralRetrospectiveESignatureConsent,
    element: (
      <ReferralWithESignatureDetailsProvider>
        <ReferralRetrospectiveESignatureConsentPage />
      </ReferralWithESignatureDetailsProvider>
    ),
  },
  {
    path: ROUTES._referralESignatureConsentPdfPreview,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <ReferralClientConsentPdfPreviewPage />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._recommendation,
    element: <ReferAFriendPage />,
  },
  {
    path: ROUTES._referralLink,
    element: <ReferralLinkPage />,
  },
  {
    path: ROUTES._referralLinkOld,
    element: <ReferralLinkPage />,
  },
  {
    path: ROUTES._rqCertifiedNdaPdf,
    element: <RqCertifiedNdaPdfPage />,
  },
  {
    path: ROUTES._userCompass,
    element: (
      <CompassGuard>
        <UserCompassPage />
      </CompassGuard>
    ),
  },
  {
    path: ROUTES._walkthrough,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <WalkthroughPage />
      </AuthGuard>
    ),
  },
  {
    path: ROUTES._xplanIntegrationRedirect,
    element: (
      <AuthGuard requiredUserType={USER_TYPE.company}>
        <LandingDarkLayout>
          <XplanIntegrationRedirectPage />
        </LandingDarkLayout>
      </AuthGuard>
    ),
  },
]

const iframeRoutes: AppRoute[] = [
  {
    path: ROUTES.accountSettingsIframe,
    element: <AccountSettingsIframePage />,
  },
  {
    path: ROUTES.mappingIframe,
    element: <MappingIframePage />,
  },
  {
    path: ROUTES.introducersIframe,
    element: <IntroducersIframePage />,
  },
  {
    path: ROUTES._referralIframe,
    element: <ReferralIframePage />,
  },
  {
    path: ROUTES._clientReferralsCompanyOverviewIframe,
    element: <ClientReferralsCompanyOverviewIframePage />,
  },
  {
    path: ROUTES._clientReferralsCompanyOverviewReferralsIframe,
    element: <ClientReferralsCompanyOverviewReferralsIframePage />,
  },
  {
    path: ROUTES._referIframe,
    element: <ReferIframePage />,
  },
  {
    path: ROUTES._referSuccessIframe,
    element: <ReferSuccessIframePage />,
  },
  {
    path: ROUTES._clientReferralsIframe,
    element: <ClientReferralsIframePage />,
  },
]

const SentryAwareRoutes = Sentry.wrapReactRouterRouting(Routes)

const Root: React.FC = () => {
  usePageViewListener()
  useRestoreScrollPosition()

  return (
    <>
      <SentryAwareRoutes>
        <Route path={ROUTES._challenge} element={<ChallengePage />} />

        {/* Embed iframe routes */}
        {iframeRoutes.map((route) => (
          <Route path={route.path} element={route.element} key={route.path} />
        ))}

        <Route element={<AppLayout />}>
          {/* Guest */}
          {guestRoutes.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}

          {/* Account setup */}
          {accountSetupRoutes.map((route) => (
            <Route
              path={route.path}
              element={<AuthGuard>{route.element}</AuthGuard>}
              key={route.path}
            />
          ))}

          {/* Group dashboard */}
          {groupDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.group}>
                  <GroupDashboardLayout padded={route.padded}>
                    {route.element}
                  </GroupDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Network dashboard */}
          {networkDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.network}>
                  <NetworkDashboardLayout padded={route.padded}>
                    {route.element}
                  </NetworkDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Company and Group dashboard routes */}
          {companyAndGroupDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard
                  guard={({ currentUserType }) =>
                    currentUserType === USER_TYPE.company ||
                    currentUserType === USER_TYPE.group
                  }
                >
                  {({ currentUserType }) => (
                    <>
                      {currentUserType === USER_TYPE.company ? (
                        <CompanyDashboardLayout padded={route.padded}>
                          {route.element}
                        </CompanyDashboardLayout>
                      ) : (
                        <GroupDashboardLayout padded={route.padded}>
                          {route.element}
                        </GroupDashboardLayout>
                      )}
                    </>
                  )}
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/*
           * Referrals CSV export. A dedicated, chrome-less page (opened in its
           * own tab) gated the same way as the referrals page, so it inherits
           * the same company/group session context.
           */}
          <Route
            path={ROUTES._referralsCsvExport}
            element={
              <AuthGuard
                guard={({ currentUserType }) =>
                  currentUserType === USER_TYPE.company ||
                  currentUserType === USER_TYPE.group
                }
              >
                <Suspense fallback={null}>
                  <ReferralsCsvExportPage />
                </Suspense>
              </AuthGuard>
            }
          />

          {/* Company dashboard */}
          {companyDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.company}>
                  <CompanyDashboardLayout
                    padded={route.padded}
                    fullscreen={route.fullscreen}
                  >
                    {route.element}
                  </CompanyDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Company dashboard company compass routes */}
          <Route element={<CompanyDashboardCompanyCompassLayout />}>
            {companyDashboardCompanyCompassRoutes.map((route) => (
              <Route
                path={route.path}
                key={route.path}
                element={route.element}
              />
            ))}
          </Route>

          {/* Company dashboard professional network routes */}
          <Route element={<CompanyDashboardProfessionalNetworkLayout />}>
            {companyDashboardProfessionalNetworkRoutes.map((route) => (
              <Route
                path={route.path}
                key={route.path}
                element={route.element}
              />
            ))}
          </Route>

          {/* Company dashboard and recommendation routes */}
          <Route element={<CompanyDashboardClientReferralsLayout />}>
            {companyDashboardRecommendationRoutes.map((route) => (
              <Route
                path={route.path}
                key={route.path}
                element={route.element}
              />
            ))}
          </Route>

          {/* Client dashboard */}
          {clientDashboardRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.client}>
                  <ClientDashboardLayout>{route.element}</ClientDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Company user landing routes */}
          {companyUserLandingRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.company}>
                  <LandingDarkLayout>{route.element}</LandingDarkLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* ICAEW */}
          {icaewRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <AuthGuard requiredUserType={USER_TYPE.icaew}>
                  <IcaewDashboardLayout>{route.element}</IcaewDashboardLayout>
                </AuthGuard>
              }
              key={route.path}
            />
          ))}

          {/* Guest landing */}
          {guestLandingRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <LandingDarkLayout>
                  <MinimalLayout>{route.element}</MinimalLayout>
                </LandingDarkLayout>
              }
              key={route.path}
            />
          ))}

          {guestLandingRoutesWthBranding.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}

          {/* Guest landing - white template */}
          {guestLandingWhiteTemplateRoutes.map((route) => (
            <Route
              path={route.path}
              element={<LandingWhiteLayout>{route.element}</LandingWhiteLayout>}
              key={route.path}
            />
          ))}

          {/* Guest landing - white template */}
          {guestLandingNoTemplateRoutes.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}

          {/* Referral invites */}
          {referralInviteRoutes.map((route) => (
            <Route
              path={route.path}
              key={route.path}
              element={
                <ReferralInviteLayout>{route.element}</ReferralInviteLayout>
              }
            />
          ))}

          {/* Office routes */}
          <Route element={<OfficeLayout />}>
            {officeRoutes.map((route) => (
              <Route
                path={route.path}
                element={route.element}
                key={route.path}
              />
            ))}
          </Route>

          {/* Misc */}
          {miscRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <SentryErrorBoundary
                  key={location.pathname}
                  variant="full-screen"
                >
                  <Suspense fallback={<LoadingWidget />}>
                    {route.element}
                  </Suspense>
                </SentryErrorBoundary>
              }
              key={route.path}
            />
          ))}

          <Route
            path={ROUTES._invalidOrExpired}
            element={<InvalidOrExpiredPage />}
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </SentryAwareRoutes>

      <GlobalModals />

      {/* Ensure react-select and react-datepicker elements are always visible
      by applying a high z-index and ensure pointer events are not blocked
      (e.g., by the <Dialog> component). */}
      <div
        id={DOM_IDS.reactSelectPortal}
        className="z-react-select pointer-events-auto relative"
      />

      <div
        id={DOM_IDS.datepickerPortal}
        className="z-datepicker pointer-events-auto relative"
      />
    </>
  )
}

export default Root
